import { BrandMark } from "./BrandMark";

interface HomeScreenProps {
  nickname: string;
  onNicknameChange: (value: string) => void;
  onQuickMatch: () => void;
  onPrivateMatch: () => void;
  onPractice: () => void;
  onWagering: () => void;
  onPlayerHub: () => void;
  onSettings: () => void;
  onlineStatus: "bootstrapping" | "ready" | "unavailable";
  resumeAvailable: boolean;
  onResume: () => void;
}

export function HomeScreen({
  nickname,
  onNicknameChange,
  onQuickMatch,
  onPrivateMatch,
  onPractice,
  onWagering,
  onPlayerHub,
  onSettings,
  onlineStatus,
  resumeAvailable,
  onResume,
}: HomeScreenProps) {
  return (
    <main className="home-screen">
      <header className="home-nav">
        <BrandMark compact />
        <div className="home-nav__actions">
          <button className="profile-button" type="button" onClick={onPlayerHub}>Profile &amp; ranks</button>
          <button className="icon-button" type="button" onClick={onSettings} aria-label="Open accessibility and audio settings">
            <SettingsIcon />
          </button>
        </div>
      </header>

      <section className="hero">
        <div className="hero__copy">
          <p className="eyebrow"><span /> Real-time 1v1 arena</p>
          <BrandMark />
          <h1>Own the angle.<br /><span>Rule the table.</span></h1>
          <p className="hero__intro">
            Fast, touch-first air hockey with immediate local control and server-authoritative online matches.
          </p>

          {resumeAvailable && (
            <button className="resume-banner" type="button" onClick={onResume}>
              <span><strong>Match still active</strong><small>Reconnect with the server-issued recovery ticket</small></span>
              <b>Resume →</b>
            </button>
          )}

          <label className="player-name">
            <span>Player name</span>
            <input
              value={nickname}
              onChange={(event) => onNicknameChange(event.target.value.slice(0, 18))}
              maxLength={18}
              autoComplete="nickname"
              aria-describedby="player-name-hint"
            />
            <small id="player-name-hint">Used in private/practice play; public queues use your verified profile name</small>
          </label>

          <div className="mode-grid" aria-label="Choose a game mode">
            <button className="mode-card mode-card--primary" type="button" onClick={onQuickMatch}>
              <span className="mode-card__icon" aria-hidden="true">⚡</span>
              <span className="mode-card__text">
                <strong>Quick match</strong>
                <small>Search the casual or ranked public queue</small>
              </span>
              <span className="mode-card__arrow" aria-hidden="true">↗</span>
            </button>
            <button className="mode-card" type="button" onClick={onPrivateMatch}>
              <span className="mode-card__icon mode-card__icon--violet" aria-hidden="true">⌁</span>
              <span className="mode-card__text">
                <strong>Private match</strong>
                <small>Create or enter a room code</small>
              </span>
              <span className="mode-card__arrow" aria-hidden="true">→</span>
            </button>
            <button className="mode-card" type="button" onClick={onPractice}>
              <span className="mode-card__icon mode-card__icon--pink" aria-hidden="true">◎</span>
              <span className="mode-card__text">
                <strong>Local practice</strong>
                <small>Play a fair reactive bot in this browser</small>
              </span>
              <span className="mode-card__arrow" aria-hidden="true">→</span>
            </button>
            <button className="mode-card" type="button" onClick={onWagering}>
              <span className="mode-card__icon mode-card__icon--violet" aria-hidden="true">$</span>
              <span className="mode-card__text">
                <strong>USDC stake (Phantom)</strong>
                <small>Fixed 10, 25, 50 or 100 USDC queues</small>
              </span>
              <span className="mode-card__arrow" aria-hidden="true">→</span>
            </button>
          </div>

          <p className="availability-note">
            Public matches use authenticated queue tickets; private rooms and local practice stay available separately.
          </p>
        </div>

        <div className="hero__visual" aria-hidden="true">
          <div className="orbit orbit--one" />
          <div className="orbit orbit--two" />
          <div className="hero-table">
            <div className="hero-table__goal hero-table__goal--top" />
            <div className="hero-table__line" />
            <div className="hero-table__center" />
            <div className="hero-table__goal hero-table__goal--bottom" />
            <div className="hero-mallet hero-mallet--top" />
            <div className="hero-mallet hero-mallet--bottom" />
            <div className="hero-puck" />
          </div>
          <div className="hero-stat hero-stat--latency"><span>LOCAL</span><strong>&lt; 1 frame</strong><small>input prediction</small></div>
          <div className="hero-stat hero-stat--physics"><span>CORE</span><strong>60 Hz</strong><small>authoritative ticks</small></div>
        </div>
      </section>

      <footer className="home-footer">
        <span><i className={`status-dot status-dot--${onlineStatus}`} /> {onlineStatus === "ready" ? "Online services ready" : onlineStatus === "bootstrapping" ? "Starting device session" : "Offline modes available"}</span>
        <span>Mouse · touch · gamepad · keyboard</span>
      </footer>
    </main>
  );
}

function SettingsIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4Z" />
      <path d="m19.4 15 .1.1a1.8 1.8 0 0 1-2.5 2.5l-.1-.1a1.8 1.8 0 0 0-3 1.3v.2a1.8 1.8 0 0 1-3.6 0v-.2a1.8 1.8 0 0 0-3-1.3l-.1.1a1.8 1.8 0 0 1-2.5-2.5l.1-.1a1.8 1.8 0 0 0-1.3-3H3.3a1.8 1.8 0 0 1 0-3.6h.2a1.8 1.8 0 0 0 1.3-3l-.1-.1a1.8 1.8 0 0 1 2.5-2.5l.1.1a1.8 1.8 0 0 0 3-1.3v-.2a1.8 1.8 0 0 1 3.6 0v.2a1.8 1.8 0 0 0 3 1.3l.1-.1a1.8 1.8 0 0 1 2.5 2.5l-.1.1a1.8 1.8 0 0 0 1.3 3h.2a1.8 1.8 0 0 1 0 3.6h-.2a1.8 1.8 0 0 0-1.3 3Z" />
    </svg>
  );
}
